import BudgetModes from '@/common/enums/budget-modes.enum';
import { formatNumber } from '@/globals';
import Validatable from '@/validator/Validatable';

export enum SelectableDimensionType {
  DEAL = 'DEAL',
  SELLER = 'SELLER',
}

export interface ISpendSteeringDimensionAPI {
  dimension_value: string;
  steering_percentage: number;
  high_priority: boolean;
}

export interface ISpendSteeringAPI {
  dimension_type: string;
  budget_mode: string;
  spend_steering_dimensions: ISpendSteeringDimensionAPI[];
}

export interface ISpendSteeringDimension {
  dimensionValue: string;
  steeringPercentage: number;
  highPriority: boolean;
}

// The minimum steering percentage is 2.5%
export const MINIMUM_STEERING_PERCENTAGE = 2.5;

export default class SpendSteering extends Validatable<SpendSteering> {
  constructor(
    public dimensionType: string = SelectableDimensionType.DEAL,
    public budgetMode: string = BudgetModes.BUDGET,
    public spendSteeringDimensions: ISpendSteeringDimension[] = [],
  ) {
    super();
  }

  get totalSteeringPercentage() {
    return this.spendSteeringDimensions.reduce(
      (acc, dimension) => (dimension.dimensionValue === '*' ? acc : acc + dimension.steeringPercentage),
      0,
    );
  }

  get totalSteeringPercentageFormatted() {
    return formatNumber(this.totalSteeringPercentage / 100, '0.00%');
  }

  // What can be used to determine available allocation for steered deals
  get availableSteeringPercentage() {
    const remaining = this.hasWildCard && this.totalSteeringPercentage > 0 ? MINIMUM_STEERING_PERCENTAGE : 0;
    const result = 100 - this.totalSteeringPercentage - remaining;
    return parseFloat(result.toFixed(2));
  }

  get remainingSteeringPercentage() {
    return 100 - this.totalSteeringPercentage;
  }

  get remainingSteeringPercentageFormatted() {
    return formatNumber(this.remainingSteeringPercentage / 100, '0.00%');
  }

  get hasWildCard() {
    return this.spendSteeringDimensions.some((dimension) => dimension.dimensionValue === '*');
  }

  clone() {
    const clonedObject = JSON.parse(JSON.stringify(this));
    return SpendSteering.fromObject(clonedObject);
  }

  toApi(): ISpendSteeringAPI {
    return {
      dimension_type: this.dimensionType,
      budget_mode: this.budgetMode,
      spend_steering_dimensions: this.spendSteeringDimensions.map((dimension: ISpendSteeringDimension) => ({
        dimension_value: String(dimension.dimensionValue),
        steering_percentage:
          dimension.dimensionValue === '*' ? this.remainingSteeringPercentage : dimension.steeringPercentage,
        high_priority: dimension.highPriority,
      })),
    };
  }

  static fromObject(obj: object) {
    return super.fromObject(obj, new SpendSteering());
  }

  static fromApi(apiObject: ISpendSteeringAPI): SpendSteering {
    return new SpendSteering(
      apiObject.dimension_type,
      apiObject.budget_mode,
      apiObject.spend_steering_dimensions
        .map((dimension) => ({
          dimensionValue: dimension.dimension_value,
          steeringPercentage: dimension.steering_percentage,
          highPriority: dimension.high_priority,
        }))
        .sort((a, b) => {
          // High priority sorting
          if (a.highPriority && !b.highPriority) return -1;
          if (!a.highPriority && b.highPriority) return 1;
          // Sort by percentage in descending order
          return b.steeringPercentage - a.steeringPercentage;
        }),
    );
  }
}
